import React from 'react';
import isNumber from 'lodash/isNumber';
import classNameBind from 'classnames/bind';
import Caption from '../Caption';
import Label from '../Label';
import { TextStyle } from '../TextStyle';
import Message from '../Message';
import Tooltip from '../Tooltip';
import Trend from './components/Trend';
import styles from './Counter.module.css';
import Sparkline from './components/Sparkline';

/* eslint-disable react/no-array-index-key */

const bindClasses = classNameBind.bind(styles);

const Counter = ({
  label,
  ranges,
  segments,
  items,
  message,
  messageType,
  data,
  seriesConfig,
  additionalMessage,
  sparklineID,
  appearance,
} = {}) => {
  const renderMessage = () => {
    if (message) {
      return (
        <Message
          message={message}
          messageType={messageType}
          vertical
          centerHorizontal
          centerVertical
          box
          additionalMessage={additionalMessage}
        />
      );
    }

    return null;
  };

  const isSegmentComparision = segments.length > 1;
  const hasSparkline = Boolean(seriesConfig);

  return items.length
    ? (
      <div
        className={
          bindClasses('wrapper', `appearance-${appearance}`, {
            'multiple-segments': isSegmentComparision,
            'with-sparkline': hasSparkline,
          })
        }
        data-ppuc-id="Counter"
      >
        {label
          && (
            <div
              className={bindClasses('metric-name', { hasSparkline })}
              data-ppuc-id="Counter.metric.name"
            >
              {label}
            </div>
          )
        }
        {ranges.map((range, rangeIndex) => (
          <div key={rangeIndex} className={bindClasses('range-container')}>
            <div
              className={bindClasses('range', { isSegmentComparision })}
              data-ppuc-id="Counter.range"
            >
              {ranges.length > 1
                && (
                  <div
                    className={bindClasses((appearance === 'new' && rangeIndex === 0 && label)
                      && 'margin-top')}
                  >
                    <Caption>{range}</Caption>
                  </div>
                )
              }
              <div
                className={bindClasses('segments')}
                data-ppuc-id="Counter.segments"
              >
                {segments.map((segment, segmentIndex) => {
                  const { count, change, reverseComparisonColor } = items[
                    rangeIndex * segments.length + segmentIndex
                  ] || {};

                  return (
                    <div
                      key={segmentIndex}
                      className={bindClasses('segment')}
                      data-ppuc-id="Counter.segment"
                    >
                      {isSegmentComparision && (
                        <div className={
                          appearance === 'new'
                            ? bindClasses(label || ranges.length > 1 ? 'margin-top' : '')
                            : bindClasses(hasSparkline && ranges.length <= 1 ? 'margin-top' : 'double-margin-top')}
                        >
                          <Tooltip content={segment}>
                            <div className={bindClasses('inline-tooltip')}>
                              <Label type="small" noWrap cutText showWhiteSpace title="">
                                <TextStyle type={`segment${segmentIndex + 1}`}>
                                  {segment}
                                </TextStyle>
                              </Label>
                            </div>
                          </Tooltip>
                        </div>
                      )}
                      <Tooltip content={count}>
                        <div
                          className={bindClasses('count', 'margin-top', 'cut-text', 'inline-tooltip')}
                          data-ppuc-id="Counter.value"
                        >
                          {count}
                        </div>
                      </Tooltip>
                      {isNumber(change)
                        && (
                          <div className={bindClasses('margin-top-half')}>
                            <Trend value={change} reverseComparisonColor={reverseComparisonColor} />
                          </div>
                        )
                      }
                    </div>
                  );
                })}
              </div>
            </div>
            {hasSparkline && (
              <div
                className={bindClasses('sparkline')}
                data-ppuc-id="Counter.sparkline"
                id={sparklineID ? `${sparklineID}${rangeIndex}` : ''}
              >
                <Sparkline data={data[rangeIndex]} seriesConfig={seriesConfig} appearance={appearance} />
              </div>
            )}
          </div>
        ))}
      </div>
    )
    : (
      <div
        className={bindClasses('zero-state')}
        data-ppuc-id="Counter.zeroState"
      >
        {renderMessage()}
      </div>
    );
};

Counter.defaultProps = {
  message: '',
  messageType: 'info',
  data: null,
  seriesConfig: null,
  additionalMessage: null,
  sparklineID: '',
  appearance: '',
};

export default Counter;
